/**
 * 学习乐园 · 算独 Mathdoku（game-suandu.js）
 * ============================================================
 * 「逻辑与推理」组新作：数独底座 + 笼算术。三档——入门 4×4（默认档，2×2 宫）、
 * 进阶 6×6（2×3 宫）、挑战 7×7（质数不分宫，纯行列+笼的拉丁方）。
 * 规则：行列（与宫）不重复之外，虚线笼内数字按运算符算出目标值。
 *
 * 出题底线（承数独「唯一解校验」）：终盘划笼后用带笼约束的计数回溯器验证
 * 「解恰好一个」；不唯一就把大笼拆小再验（拆笼只收紧约束，原解仍在，唯一性
 * 不丢）——所以页面敢承诺：卡住不是题目的错。
 *
 * 反馈口径：行列宫重复当场讲「谁和它冲突」；笼填满但算术不符当场摆算式
 * 讲「2+3+4=9，目标是 7」；「查一查」讲排除法 + 笼账本（还差多少凑出目标）。
 * 成绩口径：不计时，只按档位记完成局数，存本机 localStorage。
 *
 * 冒烟钩子：window.STSuanDu 暴露 cageValue / cageState / cageLabel /
 * makeCages / countSolutionsCaged / genPuzzle 与 state()。
 */
(function (global) {
  'use strict';

  var PREF_KEY = 'study-suandu';
  var RECORD_KEY = 'study-suandu-record';

  /* ============ 引擎：底座（复用数独约束） ============ */

  // 宫形：4×4 是 2×2 宫；6×6 是 2×3 宫；7×7 质数退化为每列一宫（= 无宫拉丁方）
  function boxDims(n) {
    if (n === 4) return [2, 2];
    if (n === 6) return [2, 3];
    if (n === 7) return [7, 1];
    return [3, 3];
  }

  function rowOf(n, idx) { return Math.floor(idx / n); }
  function colOf(n, idx) { return idx % n; }
  function boxOf(n, idx) {
    var d = boxDims(n);
    return Math.floor(rowOf(n, idx) / d[0]) * (n / d[1]) + Math.floor(colOf(n, idx) / d[1]);
  }

  function fits(n, grid, idx, v) {
    var r = rowOf(n, idx), c = colOf(n, idx), b = boxOf(n, idx);
    for (var i = 0; i < grid.length; i++) {
      if (grid[i] !== v || i === idx) continue;
      if (rowOf(n, i) === r || colOf(n, i) === c || boxOf(n, i) === b) return false;
    }
    return true;
  }

  function shuffle(arr) {
    for (var i = arr.length - 1; i > 0; i--) {
      var j = Math.floor(Math.random() * (i + 1));
      var t = arr[i]; arr[i] = arr[j]; arr[j] = t;
    }
    return arr;
  }

  function neighbors(n, idx) {
    var r = rowOf(n, idx), c = colOf(n, idx), out = [];
    if (r > 0) out.push(idx - n);
    if (r < n - 1) out.push(idx + n);
    if (c > 0) out.push(idx - 1);
    if (c < n - 1) out.push(idx + 1);
    return out;
  }

  /* ============ 引擎：笼算术 ============ */

  function cageValue(cage, vals) {
    var op = cage.op;
    if (op === '') return vals[0];
    if (op === '+') { var s = 0; for (var a = 0; a < vals.length; a++) s += vals[a]; return s; }
    if (op === '×') { var p = 1; for (var b = 0; b < vals.length; b++) p *= vals[b]; return p; }
    if (op === '-') return Math.abs(vals[0] - vals[1]);
    return vals[0] >= vals[1] ? vals[0] / vals[1] : vals[1] / vals[0];
  }

  function cageVals(cage, grid) {
    var vals = [];
    for (var k = 0; k < cage.cells.length; k++) vals.push(grid[cage.cells[k]] || 0);
    return vals;
  }

  // 笼状态：full=填满；ok=满则算术成立，未满则仍可能凑出目标（剪枝口径）
  function cageState(cage, grid) {
    var vals = cageVals(cage, grid);
    var full = vals.every(function (v) { return v > 0; });
    if (full) return { full: true, ok: cageValue(cage, vals) === cage.target };
    if (cage.op === '' || cage.op === '-' || cage.op === '÷') return { full: false, ok: cage.op !== '' };
    var sum = 0, prod = 1, empty = 0;
    for (var i = 0; i < vals.length; i++) {
      if (!vals[i]) { empty++; continue; }
      sum += vals[i]; prod *= vals[i];
    }
    if (cage.op === '+') return { full: false, ok: sum + empty <= cage.target };
    return { full: false, ok: cage.target % prod === 0 && prod <= cage.target };
  }

  function cageLabel(cage) {
    return cage.op === '' ? String(cage.target) : cage.target + cage.op;
  }

  // 带笼约束的解计数（MRV + 笼剪枝），limit 默认 2
  function countSolutionsCaged(n, grid, cages, limit) {
    var lim = limit || 2;
    var g = grid.slice();
    var byCell = {};
    for (var ci = 0; ci < cages.length; ci++) {
      for (var cj = 0; cj < cages[ci].cells.length; cj++) byCell[cages[ci].cells[cj]] = cages[ci];
    }
    var count = 0;
    function fullOk() {
      for (var c = 0; c < cages.length; c++) {
        if (!cageState(cages[c], g).ok) return false;
      }
      return true;
    }
    (function bt() {
      if (count >= lim) return;
      var best = -1, bestCands = null;
      for (var i = 0; i < g.length; i++) {
        if (g[i] !== 0) continue;
        var cands = [];
        for (var v = 1; v <= n; v++) {
          if (!fits(n, g, i, v)) continue;
          g[i] = v;
          var cage = byCell[i];
          var good = !cage || cageState(cage, g).ok;
          g[i] = 0;
          if (good) cands.push(v);
        }
        if (!cands.length) return;
        if (cands.length === 1 || best < 0 || cands.length < bestCands.length) {
          best = i; bestCands = cands;
          if (cands.length === 1) break;
        }
      }
      if (best < 0) { if (fullOk()) count++; return; }
      for (var k = 0; k < bestCands.length; k++) {
        g[best] = bestCands[k];
        bt();
        if (count >= lim) break;
      }
      g[best] = 0;
    })();
    return count;
  }

  function fullGrid(n) {
    var g = new Array(n * n);
    for (var z = 0; z < g.length; z++) g[z] = 0;
    (function bt(i) {
      if (i >= g.length) return true;
      var cands = [];
      for (var v = 1; v <= n; v++) { if (fits(n, g, i, v)) cands.push(v); }
      shuffle(cands);
      for (var k = 0; k < cands.length; k++) {
        g[i] = cands[k];
        if (bt(i + 1)) return true;
      }
      g[i] = 0;
      return false;
    })(0);
    return g;
  }

  // 给笼配运算符与目标（值来自终盘）。2 格笼从可用运算符随机取（整除才给 ÷）
  function assignOp(cage, solution) {
    var vals = cageVals(cage, solution);
    if (vals.length === 1) { cage.op = ''; cage.target = vals[0]; return; }
    if (vals.length === 2) {
      var hi = Math.max(vals[0], vals[1]), lo = Math.min(vals[0], vals[1]);
      var ops = ['+', '×', '-'];
      if (lo > 1 && hi % lo === 0) ops.push('÷');
      shuffle(ops);
      cage.op = ops[0];
    } else {
      cage.op = Math.random() < 0.5 ? '+' : '×';
    }
    cage.target = cageValue(cage, vals);
  }

  // 笼划分：随机种子 + 随机游走吸收邻格（连通），随后配运算符
  function makeCages(n, solution) {
    var cageOf = new Array(n * n);
    for (var z = 0; z < cageOf.length; z++) cageOf[z] = -1;
    var cages = [];
    var order = [];
    for (var i = 0; i < n * n; i++) order.push(i);
    shuffle(order);
    var maxCage = n <= 4 ? 3 : 4;
    for (var oi = 0; oi < order.length; oi++) {
      var seed = order[oi];
      if (cageOf[seed] >= 0) continue;
      var id = cages.length;
      var cells = [seed];
      cageOf[seed] = id;
      var want = 1 + Math.floor(Math.random() * maxCage);
      while (cells.length < want) {
        var frontier = [];
        for (var c = 0; c < cells.length; c++) {
          var nbs = neighbors(n, cells[c]);
          for (var q = 0; q < nbs.length; q++) { if (cageOf[nbs[q]] < 0) frontier.push(nbs[q]); }
        }
        if (!frontier.length) break;
        var pick = frontier[Math.floor(Math.random() * frontier.length)];
        cageOf[pick] = id;
        cells.push(pick);
      }
      var cage = { cells: cells, op: '', target: 0 };
      assignOp(cage, solution);
      cages.push(cage);
    }
    return cages;
  }

  // 一整局：满盘 → 划笼 → 唯一解校验（不唯一拆大笼重试；拆笼只收紧约束）
  function genPuzzle(n) {
    for (var attempt = 0; attempt < 8; attempt++) {
      var solution = fullGrid(n);
      var cages = makeCages(n, solution);
      for (var pass = 0; pass < 40; pass++) {
        if (countSolutionsCaged(n, new Array(n * n).fill(0), cages, 2) === 1) {
          return { n: n, solution: solution, cages: cages };
        }
        var big = -1;
        for (var b = 0; b < cages.length; b++) {
          if (cages[b].cells.length >= 2 && (big < 0 || cages[b].cells.length > cages[big].cells.length)) big = b;
        }
        if (big < 0) break;
        var cells = cages[big].cells.slice();
        var half = Math.max(1, Math.floor(cells.length / 2));
        var head = cells.slice(0, half);
        var tail = cells.slice(half);
        cages.splice(big, 1);
        var hc = { cells: head, op: '', target: 0 };
        assignOp(hc, solution);
        cages.push(hc);
        // tail 连通性：不连通就化单格（化单格只会收紧，唯一性不丢）
        var connected = tail.length <= 1;
        for (var t = 1; t < tail.length && connected; t++) {
          var adj = neighbors(n, tail[t]);
          var linked = false;
          for (var u = 0; u < t; u++) { if (adj.indexOf(tail[u]) >= 0) { linked = true; break; } }
          if (!linked) connected = false;
        }
        if (connected) {
          var tc = { cells: tail, op: '', target: 0 };
          assignOp(tc, solution);
          cages.push(tc);
        } else {
          for (var s = 0; s < tail.length; s++) {
            cages.push({ cells: [tail[s]], op: '', target: solution[tail[s]] });
          }
        }
      }
    }
    var sol = fullGrid(n);
    var single = [];
    for (var w = 0; w < sol.length; w++) single.push({ cells: [w], op: '', target: sol[w] });
    return { n: n, solution: sol, cages: single };
  }

  /* ============ 引擎：冲突与讲解 ============ */

  function conflicts(n, grid) {
    var bad = {};
    function mark(list) {
      var seen = {};
      for (var k = 0; k < list.length; k++) {
        var v = grid[list[k]];
        if (!v) continue;
        if (seen[v] !== undefined) { bad[seen[v]] = 1; bad[list[k]] = 1; }
        else seen[v] = list[k];
      }
    }
    for (var r = 0; r < n; r++) {
      var row = []; for (var c = 0; c < n; c++) row.push(r * n + c);
      mark(row);
      var col = []; for (var cc = 0; cc < n; cc++) col.push(cc * n + r);
      mark(col);
    }
    var d = boxDims(n);
    if (d[1] > 1) {
      for (var b = 0; b < n * n / (d[0] * d[1]); b++) {
        var zone = [];
        for (var i = 0; i < grid.length; i++) { if (boxOf(n, i) === b) zone.push(i); }
        mark(zone);
      }
    }
    return Object.keys(bad).map(Number);
  }

  // 查一查：错格的排除法 + 笼账本
  function explainCell(n, grid, solution, cages, idx) {
    var r = rowOf(n, idx), c = colOf(n, idx);
    var rowHas = [], colHas = [];
    for (var i = 0; i < grid.length; i++) {
      if (!grid[i] || i === idx) continue;
      if (rowOf(n, i) === r) rowHas.push(grid[i]);
      if (colOf(n, i) === c) colHas.push(grid[i]);
    }
    rowHas.sort(function (a, b) { return a - b; });
    colHas.sort(function (a, b) { return a - b; });
    var head = '第 ' + (r + 1) + ' 行第 ' + (c + 1) + ' 列填「' + grid[idx] + '」不对，这格该填「' + solution[idx] + '」。';
    var cageInfo = '';
    for (var k = 0; k < cages.length; k++) {
      if (cages[k].cells.indexOf(idx) < 0) continue;
      var cage = cages[k];
      var filled = [], sum = 0, prod = 1, empty = 0;
      for (var q = 0; q < cage.cells.length; q++) {
        var v = grid[cage.cells[q]];
        if (v) { filled.push(v); sum += v; prod *= v; } else empty++;
      }
      var book;
      if (cage.op === '+') book = empty ? '已填 ' + filled.join('、') + '，还差 ' + (cage.target - sum) + ' 凑出目标' : '算式 ' + filled.join('+') + '=' + sum;
      else if (cage.op === '×') book = empty ? '已填 ' + filled.join('、') + '，乘积 ' + prod + '，目标是 ' + cage.target : '算式 ' + filled.join('×') + '=' + prod;
      else book = '两数 ' + filled.join('、') + '，目标是 ' + cage.target + cage.op;
      cageInfo = '它所在的笼（' + cageLabel(cage) + '）：' + book + '。';
      break;
    }
    return head + '排除法：这一行已有 ' + rowHas.join('、') + '；这一列已有 ' + colHas.join('、')
      + '。' + cageInfo + '行、列、笼三边一起卡，这格的候选就锁死了。';
  }

  /* ============ UI ============ */

  var LEVELS = [
    { n: 4, name: '入门 4×4', hint: '2×2 宫 · 默认档' },
    { n: 6, name: '进阶 6×6', hint: '2×3 宫' },
    { n: 7, name: '挑战 7×7', hint: '无宫拉丁方' }
  ];

  function el(tag, cls, text) {
    var node = document.createElement(tag);
    if (cls) node.className = cls;
    if (text != null) node.textContent = text;
    return node;
  }

  var root = null;
  var ui = {};
  var S = {
    n: 4, solution: null, cages: [], grid: [], sel: -1,
    ended: false, soundOn: true, resetArm: false, resetTimer: null
  };

  function renderLevels() {
    var box = ui.levels;
    box.textContent = '';
    for (var i = 0; i < LEVELS.length; i++) {
      (function (lv) {
        var b = el('button', 'st-llk-pack-btn');
        b.type = 'button';
        b.setAttribute('data-level', String(lv.n));
        b.setAttribute('role', 'radio');
        b.appendChild(el('span', 'st-llk-pack-name', lv.name));
        b.appendChild(el('small', 'st-llk-pack-hint', lv.hint));
        b.addEventListener('click', function () {
          if (S.n === lv.n) return;
          newGame(lv.n);
        });
        box.appendChild(b);
      })(LEVELS[i]);
    }
  }

  function syncLevels() {
    var btns = ui.levels.querySelectorAll('[data-level]');
    for (var i = 0; i < btns.length; i++) {
      var on = Number(btns[i].getAttribute('data-level')) === S.n;
      btns[i].classList.toggle('is-active', on);
      btns[i].setAttribute('aria-checked', on ? 'true' : 'false');
    }
  }

  function cageByIdx() {
    var map = {};
    for (var c = 0; c < S.cages.length; c++) {
      for (var k = 0; k < S.cages[c].cells.length; k++) map[S.cages[c].cells[k]] = S.cages[c];
    }
    return map;
  }

  function buildBoard() {
    var board = ui.board;
    board.textContent = '';
    board.style.setProperty('--st-sd-cols', String(S.n));
    var d = boxDims(S.n);
    var map = cageByIdx();
    for (var idx = 0; idx < S.grid.length; idx++) {
      (function (i) {
        var b = el('button', 'st-sd-cell st-md-cell');
        b.type = 'button';
        b.setAttribute('data-idx', String(i));
        var r = rowOf(S.n, i), c = colOf(S.n, i);
        if (d[1] > 1 && c > 0 && c % d[1] === 0) b.classList.add('bx');
        if (d[0] < S.n && r > 0 && r % d[0] === 0) b.classList.add('by');
        var cage = map[i];
        if (c === 0 || map[i - 1] !== cage) b.classList.add('cg-l');
        if (c === S.n - 1 || map[i + 1] !== cage) b.classList.add('cg-r');
        if (r === 0 || map[i - S.n] !== cage) b.classList.add('cg-t');
        if (r === S.n - 1 || map[i + S.n] !== cage) b.classList.add('cg-b');
        if (cage && cage.cells[0] === i) b.appendChild(el('span', 'st-md-cage', cageLabel(cage)));
        b.appendChild(el('span', 'st-md-num', ''));
        b.addEventListener('click', function () { onCell(i); });
        board.appendChild(b);
      })(idx);
    }
    renderPad();
    renderCells();
  }

  function renderPad() {
    var pad = ui.pad;
    pad.textContent = '';
    for (var v = 1; v <= S.n; v++) {
      (function (val) {
        var b = el('button', 'st-sd-key');
        b.type = 'button';
        b.setAttribute('data-val', String(val));
        b.textContent = String(val);
        b.addEventListener('click', function () { onKey(val); });
        pad.appendChild(b);
      })(v);
    }
    var clear = el('button', 'st-sd-key st-sd-key--clear');
    clear.type = 'button';
    clear.setAttribute('data-val', '0');
    clear.textContent = '清除';
    clear.addEventListener('click', function () { onKey(0); });
    pad.appendChild(clear);
  }

  function renderCells() {
    var bad = {};
    var cl = conflicts(S.n, S.grid);
    for (var k = 0; k < cl.length; k++) bad[cl[k]] = 1;
    var badCage = {};
    for (var c = 0; c < S.cages.length; c++) {
      var st = cageState(S.cages[c], S.grid);
      if (st.full && !st.ok) {
        for (var q = 0; q < S.cages[c].cells.length; q++) badCage[S.cages[c].cells[q]] = 1;
      }
    }
    var cells = ui.board.children;
    for (var i = 0; i < cells.length; i++) {
      var node = cells[i];
      node.querySelector('.st-md-num').textContent = S.grid[i] ? String(S.grid[i]) : '';
      node.classList.toggle('is-user', S.grid[i] !== 0);
      node.classList.toggle('is-bad', !!bad[i] || !!badCage[i]);
      node.classList.toggle('is-sel', S.sel === i);
      node.setAttribute('aria-label', '第 ' + (rowOf(S.n, i) + 1) + ' 行第 ' + (colOf(S.n, i) + 1) + ' 列，'
        + (S.grid[i] ? '你填了 ' + S.grid[i] : '空格'));
    }
    renderStatus();
  }

  function renderStatus() {
    if (S.ended) return;
    var filled = 0;
    for (var i = 0; i < S.grid.length; i++) { if (S.grid[i]) filled++; }
    ui.status.className = 'st-status';
    ui.status.textContent = '入门 ' + S.n + '×' + S.n + ' · 笼 ' + S.cages.length + ' 个 · 已填 ' + filled + '/' + S.grid.length;
  }

  function renderNote(text, tone) {
    ui.note.textContent = text || '';
    ui.note.className = 'st-llk-note' + (tone ? ' is-' + tone : '');
  }

  function renderRecord() {
    var box = ui.record;
    box.textContent = '';
    var rec = loadRecord();
    for (var i = 0; i < LEVELS.length; i++) {
      var lv = LEVELS[i];
      var r = rec['x' + lv.n];
      box.appendChild(recordStat(
        lv.n + '×' + lv.n + '完成',
        (r && r.wins ? r.wins + ' 局' : '还没通关过——填满一次就记在这里')
      ));
    }
  }

  function recordStat(label, value) {
    var d = el('div', 'st-llk-stat');
    d.appendChild(el('span', 'st-llk-stat-label', label));
    d.appendChild(el('strong', 'st-llk-stat-value', value));
    return d;
  }

  /* 手机外放响度补偿（口径统一，由 scripts/boost-sfx-mobile.mjs 批量写入）：
     手机小喇叭对 500Hz 以下钝感、超短音感知响度低——触屏设备把提示音增益翻倍、
     低于 500Hz 的音移高八度、衰减拉长；桌面/耳机维持原响度。 */
  var SFX_MOBILE = false;
  try {
    SFX_MOBILE = (global.matchMedia && global.matchMedia('(pointer: coarse)').matches) ||
      (global.navigator && global.navigator.maxTouchPoints > 0);
  } catch (e) { /* 检测不可用按桌面处理 */ }
  var actx = null;
  function sfx(kind) {
    if (!S.soundOn) return;
    try {
      var AC = global.AudioContext || global.webkitAudioContext;
      if (!AC) return;
      if (!actx) actx = new AC();
      if (actx.state === 'suspended' && actx.resume) actx.resume();
      var t = actx.currentTime;
      var seq;
      if (kind === 'ok') seq = [[523, 0], [659, 0.07]];
      else if (kind === 'win') seq = [[523, 0], [659, 0.1], [784, 0.2], [1046, 0.3]];
      else if (kind === 'bad') seq = [[196, 0], [185, 0.09]];
      else seq = [[330, 0]];
      for (var i = 0; i < seq.length; i++) {
        var o = actx.createOscillator();
        var g = actx.createGain();
        o.type = 'triangle';
        o.frequency.setValueAtTime(SFX_MOBILE && seq[i][0] < 500 ? seq[i][0] * 2 : seq[i][0], t + seq[i][1]);
        g.gain.setValueAtTime(0.0001, t + seq[i][1]);
        g.gain.exponentialRampToValueAtTime(SFX_MOBILE ? 0.42 : 0.18, t + seq[i][1] + 0.01);
        g.gain.exponentialRampToValueAtTime(0.0001, t + seq[i][1] + (SFX_MOBILE ? 0.2 : 0.14));
        o.connect(g); g.connect(actx.destination);
        o.start(t + seq[i][1]); o.stop(t + seq[i][1] + (SFX_MOBILE ? 0.26 : 0.16));
      }
    } catch (e) { /* 音频不可用静默 */ }
  }

  function onCell(idx) {
    if (S.ended) return;
    S.sel = (S.sel === idx) ? -1 : idx;
    sfx('sel');
    renderCells();
    if (S.sel >= 0 && !S.grid[idx]) {
      var cage = cageByIdx()[idx];
      renderNote('选中了第 ' + (rowOf(S.n, idx) + 1) + ' 行第 ' + (colOf(S.n, idx) + 1) + ' 列：'
        + (cage ? '它属于笼「' + cageLabel(cage) + '」（' + cage.cells.length + ' 格）——' : '')
        + '点下面的数字填入；再点这格或「清除」可以清空。', '');
    }
  }

  function onKey(v) {
    if (S.ended) return;
    if (S.sel < 0) {
      renderNote('先点盘上的空格选中它，再点数字填入。', '');
      return;
    }
    var idx = S.sel;
    if (!v) {
      S.grid[idx] = 0;
      renderCells();
      renderNote('清掉了。想想这格的行列缺什么、笼还差多少。', '');
      return;
    }
    S.grid[idx] = v;
    S.sel = -1;
    if (isComplete()) {
      renderCells();
      winGame();
      return;
    }
    var cl = conflicts(S.n, S.grid);
    if (cl.indexOf(idx) >= 0) {
      var r = rowOf(S.n, idx), c = colOf(S.n, idx);
      var where = [];
      for (var i = 0; i < S.grid.length; i++) {
        if (i === idx || S.grid[i] !== v) continue;
        if (rowOf(S.n, i) === r) where.push('第 ' + (r + 1) + ' 行');
        else if (colOf(S.n, i) === c) where.push('第 ' + (c + 1) + ' 列');
      }
      sfx('bad');
      renderCells();
      renderNote('「' + v + '」和' + where.join('、') + '里已有的「' + v + '」冲突了——'
        + (S.n === 7 ? '同一行、同一列' : '同一行、同一列、同一个宫') + '里不能有两个 ' + v + '。', 'bad');
      return;
    }
    var cage = cageByIdx()[idx];
    var st = cage ? cageState(cage, S.grid) : null;
    renderCells();
    if (st && st.full && !st.ok) {
      sfx('bad');
      var vals = cageVals(cage, S.grid);
      renderNote('笼「' + cageLabel(cage) + '」填满了但算不对：' + vals.join(cage.op === '+' ? '+' : cage.op === '×' ? '×' : cage.op)
        + ' 不等于 ' + cage.target + '。这笼里有格子填错了——先用行列排除缩小范围。', 'bad');
      return;
    }
    sfx('ok');
    renderNote('填上了「' + v + '」。行列没冲突'
      + (cage ? '，笼「' + cageLabel(cage) + '」' + (st.full ? '成立' : '还没满，继续凑') : '')
      + '——但正确还要自己推：行、列、笼三边一起卡。', '');
  }

  function isComplete() {
    for (var i = 0; i < S.grid.length; i++) {
      if (S.grid[i] !== S.solution[i]) return false;
    }
    return true;
  }

  function checkBoard() {
    if (S.ended) return;
    var wrong = -1;
    for (var i = 0; i < S.grid.length; i++) {
      if (S.grid[i] && S.grid[i] !== S.solution[i]) { wrong = i; break; }
    }
    if (wrong < 0) {
      var empty = 0;
      for (var j = 0; j < S.grid.length; j++) { if (!S.grid[j]) empty++; }
      renderNote(empty
        ? '盘面上还没发现填错的数——接下来找「唯一候选」：哪行/列只缺一个数就直接定它；笼里只剩一格没填时，用目标倒推（差多少填多少）。'
        : '空格都还没填，先从单格笼和只缺一格的行下手最快。', '');
      return;
    }
    renderNote(explainCell(S.n, S.grid, S.solution, S.cages, wrong), 'bad');
  }

  function winGame() {
    S.ended = true;
    sfx('win');
    var rec = loadRecord();
    var key = 'x' + S.n;
    var cur = rec[key];
    if (!cur || typeof cur !== 'object' || Array.isArray(cur)) cur = { wins: 0 };
    cur.wins++;
    rec[key] = cur;
    try { localStorage.setItem(RECORD_KEY, JSON.stringify(rec)); } catch (e) { /* 隐私模式静默 */ }
    ui.status.className = 'st-status is-over';
    ui.status.textContent = '完成！' + S.n + '×' + S.n + ' 这一局解出来了——算独不计时，解出来就是赢。点「新开一局」再来一盘。';
    renderNote('算独的三板斧：行列排除（数独老底子）、笼倒推（差多少填多少、乘积必须是目标的约数）、单格笼即提示。7×7 只是把它们用更多遍。', 'ok');
    renderRecord();
    renderButtons();
  }

  function loadRecord() {
    try {
      var r = JSON.parse(localStorage.getItem(RECORD_KEY) || '{}');
      return r && typeof r === 'object' && !Array.isArray(r) ? r : {};
    } catch (e) { return {}; }
  }

  function resetRecord() {
    if (!S.resetArm) {
      S.resetArm = true;
      renderButtons();
      if (S.resetTimer) clearTimeout(S.resetTimer);
      S.resetTimer = setTimeout(function () { S.resetArm = false; S.resetTimer = null; renderButtons(); }, 3000);
      return;
    }
    S.resetArm = false;
    if (S.resetTimer) { clearTimeout(S.resetTimer); S.resetTimer = null; }
    try { localStorage.removeItem(RECORD_KEY); } catch (e) { /* 隐私模式静默 */ }
    renderRecord();
    renderButtons();
    toast('已重置算独的本机成绩');
  }

  function renderButtons() {
    ui.resetBtn.classList.toggle('is-arm', S.resetArm);
    ui.resetBtn.textContent = S.resetArm ? '再点一次确认重置' : '重置成绩';
  }

  function save() {
    try {
      localStorage.setItem(PREF_KEY, JSON.stringify({ v: 1, n: S.n, soundOn: S.soundOn }));
    } catch (e) { /* 存储不可用静默 */ }
  }

  function restorePrefs() {
    try {
      var d = JSON.parse(localStorage.getItem(PREF_KEY) || '{}');
      if (!d || d.v !== 1) return;
      if (d.n === 4 || d.n === 6 || d.n === 7) S.n = d.n;
      S.soundOn = d.soundOn !== false;
    } catch (e) { /* 忽略脏数据 */ }
  }

  function newGame(n) {
    var p = genPuzzle(n);
    S.n = n;
    S.solution = p.solution;
    S.cages = p.cages;
    S.grid = new Array(n * n);
    for (var z = 0; z < S.grid.length; z++) S.grid[z] = 0;
    S.sel = -1;
    S.ended = false;
    S.resetArm = false;
    if (S.resetTimer) { clearTimeout(S.resetTimer); S.resetTimer = null; }
    syncLevels();
    buildBoard();
    renderRecord();
    renderButtons();
    renderNote('「入门 ' + n + '×' + n + '」：每个虚线笼按运算符算出目标值，行、列' + (n === 7 ? '' : '、宫') + '不重复。点空格再点数字填入——每局都验证过只有唯一解。', '');
    save();
  }

  function bindSoundPicker() {
    var btns = root.querySelectorAll('[data-st-sound]');
    for (var i = 0; i < btns.length; i++) {
      (function (b) {
        b.addEventListener('click', function () {
          S.soundOn = b.getAttribute('data-st-sound') === 'on';
          for (var j = 0; j < btns.length; j++) {
            var on = btns[j] === b;
            btns[j].classList.toggle('is-active', on);
            btns[j].setAttribute('aria-checked', on ? 'true' : 'false');
          }
          save();
        });
      })(btns[i]);
    }
    syncSoundPicker();
  }

  function syncSoundPicker() {
    var btns = root.querySelectorAll('[data-st-sound]');
    for (var i = 0; i < btns.length; i++) {
      var on = (btns[i].getAttribute('data-st-sound') === 'on') === S.soundOn;
      btns[i].classList.toggle('is-active', on);
      btns[i].setAttribute('aria-checked', on ? 'true' : 'false');
    }
  }

  function toast(msg) {
    if (!ui.saved) return;
    ui.saved.textContent = msg;
    ui.saved.hidden = false;
    clearTimeout(toast._t);
    toast._t = setTimeout(function () { ui.saved.hidden = true; }, 2200);
  }

  function boot() {
    root = document.querySelector('[data-st-game]');
    if (!root) return;
    ui.levels = root.querySelector('[data-st-md-levels]');
    ui.board = root.querySelector('[data-st-sd-board]');
    ui.pad = root.querySelector('[data-st-sd-pad]');
    ui.status = root.querySelector('[data-st-status]');
    ui.note = root.querySelector('[data-st-note]');
    ui.record = root.querySelector('[data-st-record]');
    ui.saved = root.querySelector('[data-st-saved]');
    ui.newBtn = root.querySelector('[data-st-new]');
    ui.checkBtn = root.querySelector('[data-st-check]');
    ui.resetBtn = root.querySelector('[data-st-reset]');

    restorePrefs();
    renderLevels();
    bindSoundPicker();

    ui.newBtn.addEventListener('click', function () { newGame(S.n); });
    ui.checkBtn.addEventListener('click', checkBoard);
    ui.resetBtn.addEventListener('click', resetRecord);

    newGame(S.n);
  }

  if (typeof document !== 'undefined') {
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
    else boot();
  }

  /* ============ 冒烟与复用钩子 ============ */

  global.STSuanDu = {
    boxDims: boxDims,
    cageValue: cageValue,
    cageState: cageState,
    cageLabel: cageLabel,
    makeCages: makeCages,
    countSolutionsCaged: countSolutionsCaged,
    genPuzzle: genPuzzle,
    conflicts: conflicts,
    explainCell: explainCell,
    state: function () { return { n: S.n, solution: S.solution, cages: S.cages, grid: S.grid }; }
  };
})(typeof window !== 'undefined' ? window : globalThis);
